import { gql } from "@apollo/client";
import { faPeopleGroup, faPerson, faSackDollar } from "@fortawesome/free-solid-svg-icons";
import HTMLCode from "components/common/HTMLCode";
import PageView, { PageViewPropsType } from "components/common/page/PageView";
import DateView from "components/common/views/DateView";
import { ItemViewPropsType } from "components/common/views/ItemView";
import MoneyView from "components/common/views/MoneyView";
import { AppRoutes } from "config/router";
import { AccommodationMaintenanceViewPageQuery } from "types/graphql";
import { formatMoney } from "utilities/datalist/datalist-sort-utilities";
import useAppContext from "utilities/hooks/useAppContext";
import * as lodash from "lodash";
import PersonPropertyView from "components/entities/app/person/PersonPropertyView";
import RoomTypeView from "components/entities/accommodation/roomType/RoomTypeView";
import { useParams } from "react-router-dom";

const query = gql`
query AccommodationMaintenanceViewPage($id: Int!) {
    accommodationWorkMaintenance(id: $id) {
      id
      note
      workers {
        items {
          person {
            name
            id,
          }
          date
          price
          id
        }
      }
      season {
        name
        id
      }
      startDate
      endDate
      name
      logs {
        items {
          maintenanceType {
            name
            id
          }
          investment {
            name
            id
            total
            investmentType
            startDate
          }
          id
          logRooms {
            items {
              room {
                roomType {
                  color
                  name
                }
                name
              }
            }
          }
        }
      }
    }
  }  
`;

type ViewModel = NonNullable<AccommodationMaintenanceViewPageQuery['accommodationWorkMaintenance']>;

const AccommodationMaintenanceViewPage = () => {
    const context = useAppContext();
    const { id } = useParams<{ id: string }>();

    const pageConfig: PageViewPropsType<AccommodationMaintenanceViewPageQuery> = {
        query,
        queryDataPropertyName: 'accommodationWorkMaintenance',
        title: 'pages.accommodation.work.maintenance.view',
        name: 'accommodationMaintenanceViewPage',
        editUrl: AppRoutes.ACCOMMODATION_MAINTENANCE_EDIT,
        onDelete: 'accommodationWorkMaintenanceDelete',
        view: i => view(i.accommodationWorkMaintenance!),
    }

    const view = (model: ViewModel) => {
        let logs: ItemViewPropsType['model'][0]['items'] = [];

        model.logs.items.forEach((l, i) => {
            logs = [
                ...logs,
				{
					label: '',
					value: i > 0 ? <div className="border-b border-dashed theme-border" /> : <></>,
					name: 'br-' + i
				},
                {
                    label: 'fields.type.shortName',
                    value: l.maintenanceType.name,
                    name: 'type-' + i
                },
                {
                    label: 'fields.investment.shortName',
                    value: l.investment ? (<>
                        <span>{l.investment.name}</span>
                        <HTMLCode />
                        <MoneyView value={l.investment.total} />
                        <HTMLCode />
                        -
                        <HTMLCode />
                        <i>{context.t('agriculture.work.investmentTypes.' + l.investment.investmentType.toLowerCase())}</i>
                    </>) : '-',
                    name: 'investment-' + i
                },
                {
                    label: 'fields.group.shortName',
                    value: <>
                        {l.logRooms.items.map(r => (
                            <RoomTypeView 
                                key={`${id}-${l.id}-${r.room.name}-room`} 
                                name={r.room.name} 
                                color={r.room.roomType.color} 
                            />
                        ))}
                    </>, //l.logRooms.items.map(g => g.room.name).join(', '),
                    name: 'group-' + i
                }
            ]
        })

		const data: ItemViewPropsType['model'] = [
			{
				items: [
					{
						label: 'fields.id.shortName',
						value: model.id,
						hide: 'compact',
						name: "id"
					},
					{
						label: 'fields.name.shortName',
						value: model.name,
						name: "name"
					},
					{
						label: 'fields.date.shortName',
						value: <DateView date={model.startDate} endDate={model.endDate} />,
						name: "date"
					},
				],
				name: 'id',
			},
			{
				name: 'logs',
				items: logs
			},
			{
				name: 'workers-data',
				items: [
					{
						icon: faPeopleGroup,
						label: 'fields.workersCount.name',
						name: 'workersCount',
						value: model.workers.items.length
					},
					{
						icon: faSackDollar,
						label: 'fields.workersSum.name',
						name: 'workersSum',
						value: <>{formatMoney(lodash.sumBy(model.workers.items, i => i.price))} <HTMLCode code="euro" /></>
					},
					{
						icon: faPeopleGroup,
						label: 'fields.workers.shortName',
						name: 'workers',
						hide: 'compact',
						value: ' '
					},
					...(model.workers.items.map((w, i) => {
                        var personName = `${i + 1}. ${w?.person?.name} - ${formatMoney(w?.price)} €`;
						return {
							icon: faPerson,
							label: '',
							name: 'person-' + i,
							hide: 'compact',
							value: <PersonPropertyView name={`${w.person.id}-person-view-accommodation-maintenance-view-page`} personName={personName} personId={w.person.id} />  // <>{i + 1}. {w?.person?.name}, <i>{formatMoney(w?.price)} <HTMLCode code="euro" /></i></>
						}
					})) as any,
				],
				className: {
					compact: 'grid text-center grid-cols-2'
				}
			},
			{
				name: 'notes',
				items: [
					{
						label: 'fields.note.name',
						name: 'note',
						value: model.note,
						hide: "compact",
						propertyType: 'note'
					}
				]
			}
		];

        return data;
    }

    return (
        <PageView {...pageConfig} />
    );
};

export default AccommodationMaintenanceViewPage;
